import { MESSAGE_TYPE_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import {
  getGraphReportAggregateTypeOptions,
  getMetricReportAggregateTypeOptions,
} from '@/app/dashboards/utils/dashboard-general.util';
import { REPORT_CATEGORY_OPTIONS } from '@/app/dashboards/utils/reports.enum';
import {
  AGGREGATE_REPORT_TYPES,
  BASE_BREAKDOWN_OPTIONS,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES,
  GRAPH_DISPLAY_OPTIONS,
  GRAPH_DISPLAY_OPTIONS_FOR_MENU,
  METRIC_DISPLAY_OPTIONS,
  REPORT_PREVIEW_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';

// Types
export interface BreakdownConfig {
  secondary_breakdown_options: string[];
}

export interface BreakdownOptions {
  [key: string]: BreakdownConfig;
}

export interface AggregationOption {
  value: string;
  label: string;
  text?: string;
  icon?: string;
  formattedValue?: string;
  hideByVisualizationTypes?: string[];
  [key: string]: unknown;
}

export enum VISUALIZATION_OPTIONS {
  DOUGHNUT_CHART = 'DOUGHNUT_CHART',
  GRAPH = 'GRAPH',
  HORIZONTAL_BAR_CHART = 'HORIZONTAL_BAR_CHART',
  LINE_CHART = 'LINE_CHART',
  SINGLE_BAR_GRAPH = 'SINGLE_BAR_GRAPH',
  STACKED_BAR_GRAPH = 'STACKED_BAR_GRAPH',
  TABLE = 'TABLE',
  TOTAL = 'TOTAL',
  VERTICAL_BAR_CHART = 'VERTICAL_BAR_CHART',
}

export const VISUALIZATION_OPTION_DISPLAY_NAMES: Record<string, string> = Object.freeze({
  DOUGHNUT_CHART: 'Doughnut Chart',
  GRAPH: 'Graph',
  HORIZONTAL_BAR_CHART: 'Horizontal Bar Chart',
  LINE_CHART: 'Line Chart',
  SINGLE_BAR_GRAPH: 'Single Bar Chart',
  STACKED_BAR_GRAPH: 'Stacked Bar Chart',
  TABLE: 'Table',
  TOTAL: 'Aggregate Total Report',
  VERTICAL_BAR_CHART: 'Vertical Bar Chart',
});

// Map backend aggregation keys to enum objects for different report categories
const PRIMARY_AGGREGATION_MAP: Record<string, AggregationOption> = Object.freeze({
  TOTAL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOTAL,
  AGE_GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE_GENDER,
  AGE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE,
  ASSIGNEE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.ASSIGNEE,
  BOARD: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BOARD,
  BRAND: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND,
  BRAND_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND_TAG,
  CAMPAIGN: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CAMPAIGN,
  CHANNEL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CHANNEL,
  CITY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CITY,
  COMPETITOR_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.COMPETITOR_TAG,
  CONTENT_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CONTENT_TAG,
  COUNTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.COUNTRY,
  CREATOR_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CREATOR_TAG,
  GALLERY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.GALLERY,
  GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.GENDER,
  INDUSTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.INDUSTRY,
  MEDIA_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  MENTION_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MENTION_TYPE,
  MESSAGE_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TAG,
  MESSAGE_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TYPE,
  PUBLISHER_PLATFORM: REPORT_PREVIEW_AGGREGATE_BY_TYPES.PUBLISHER_PLATFORM,
  REGION: REPORT_PREVIEW_AGGREGATE_BY_TYPES.REGION,
  SENTIMENT: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SENTIMENT,
  SOURCE_CAMPAIGN: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN,
  STATE_PROVINCE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TAG,
  TOPIC: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOPIC,
  FOLLOWER_STATUS: REPORT_PREVIEW_AGGREGATE_BY_TYPES.FOLLOWER_STATUS,
});

// Map backend secondary breakdown keys to enum objects
const SECONDARY_BREAKDOWN_MAP: Record<string, AggregationOption> = Object.freeze({
  NONE: BASE_BREAKDOWN_OPTIONS.NONE,
  AGE: BASE_BREAKDOWN_OPTIONS.AGE,
  ASSIGNEE: BASE_BREAKDOWN_OPTIONS.ASSIGNEE,
  BOARD: BASE_BREAKDOWN_OPTIONS.BOARD,
  BRAND: BASE_BREAKDOWN_OPTIONS.BRAND,
  BRAND_TAG: BASE_BREAKDOWN_OPTIONS.BRAND_TAG,
  CAMPAIGN: BASE_BREAKDOWN_OPTIONS.CAMPAIGN,
  CHANNEL: BASE_BREAKDOWN_OPTIONS.CHANNEL,
  CITY: BASE_BREAKDOWN_OPTIONS.CITY,
  COMPETITOR_TAG: BASE_BREAKDOWN_OPTIONS.COMPETITOR_TAG,
  CONTENT_TAG: BASE_BREAKDOWN_OPTIONS.CONTENT_TAG,
  COUNTRY: BASE_BREAKDOWN_OPTIONS.COUNTRY,
  CREATOR_TAG: BASE_BREAKDOWN_OPTIONS.CREATOR_TAG,
  GALLERY: BASE_BREAKDOWN_OPTIONS.GALLERY,
  GENDER: BASE_BREAKDOWN_OPTIONS.GENDER,
  INDUSTRY: BASE_BREAKDOWN_OPTIONS.INDUSTRY,
  MEDIA_TYPE: BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE,
  MENTION_TYPE: BASE_BREAKDOWN_OPTIONS.MENTION_TYPE,
  MESSAGE_TYPE: BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE,
  SENTIMENT: BASE_BREAKDOWN_OPTIONS.SENTIMENT,
  STATE_PROVINCE: BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE,
  TAG: BASE_BREAKDOWN_OPTIONS.TAG,
  TOPIC: BASE_BREAKDOWN_OPTIONS.TOPIC,
  FOLLOWER_STATUS: BASE_BREAKDOWN_OPTIONS.FOLLOWER_STATUS,
});

// Remove when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
export const legacyCategoryByVisualizationType: Record<string, VISUALIZATION_OPTIONS[]> = {
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
    VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH,
  ],
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    VISUALIZATION_OPTIONS.HORIZONTAL_BAR_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    VISUALIZATION_OPTIONS.DOUGHNUT_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.EARNED.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
    VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH,
  ],
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
  ],
};

// Default visualization set shared by most categories. Stacked is omitted
// because StackedBarBreakdown + secondary_aggregate_by handles it.
const DEFAULT_CATEGORY_VISUALIZATIONS: VISUALIZATION_OPTIONS[] = [
  VISUALIZATION_OPTIONS.LINE_CHART,
  VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  VISUALIZATION_OPTIONS.TABLE,
  VISUALIZATION_OPTIONS.TOTAL,
];

export const categoryByVisualizationType: Record<string, VISUALIZATION_OPTIONS[]> = {
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.EARNED.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    ...DEFAULT_CATEGORY_VISUALIZATIONS,
    VISUALIZATION_OPTIONS.HORIZONTAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    ...DEFAULT_CATEGORY_VISUALIZATIONS,
    VISUALIZATION_OPTIONS.DOUGHNUT_CHART,
  ],
};

const CATEGORIES_WITHOUT_GRAPH_LINE_STACKED_BREAKDOWNS: string[] = [
  REPORT_CATEGORY_OPTIONS.OWNED.id,
  REPORT_CATEGORY_OPTIONS.COMMUNITY.id,
  REPORT_CATEGORY_OPTIONS.TSI.id,
  REPORT_CATEGORY_OPTIONS.EARNED.id,
  REPORT_CATEGORY_OPTIONS.COMPETITIVE.id,
];

/**
 * Get list of available primary aggregation keys from backend breakdown options
 */
export function getAvailablePrimaryAggregations(
  breakdownOptions: BreakdownOptions | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object') {
    return [];
  }

  return Object.keys(PRIMARY_AGGREGATION_MAP).filter((key) =>
    Object.keys(breakdownOptions).includes(key),
  );
}

interface BreakdownOptionsBag {
  featureFlags?: FeatureFlags;
  ensureAlwaysShown?: boolean;
}

/**
 * Build primary aggregation options from backend breakdown options
 * Maps backend keys to full enum objects with labels, icons, etc.
 * Pass `ensureAlwaysShown: true` to keep CONTENT_TAG / MEDIA_TYPE present even
 * when the metric omits them, so the dropdown can render them disabled with a
 * tooltip instead of hiding them.
 */
export function buildPrimaryAggregationOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
  { featureFlags = {}, ensureAlwaysShown = false }: BreakdownOptionsBag = {},
): AggregationOption[] {
  const primaryKeys = getAvailablePrimaryAggregations(breakdownOptions);

  const mappedOptions = primaryKeys
    .map((key) => {
      const enumObject = PRIMARY_AGGREGATION_MAP[key];
      return enumObject;
    })
    .filter(Boolean); // Remove undefined values

  if (!ensureAlwaysShown) return mappedOptions;
  return ensureAlwaysShownDimensions(mappedOptions, PRIMARY_AGGREGATION_MAP, featureFlags);
}

interface BuildNestedOptions extends BreakdownOptionsBag {
  enableLineChartBreakdown?: boolean;
}

// metrics objects and breakdown options from backend
export function buildNestedVisualizationOptionsByCategory(
  breakdownOptions: BreakdownOptions | null | undefined,
  categoryVisualizationMap: Record<string, VISUALIZATION_OPTIONS[]> = categoryByVisualizationType,
  options: BuildNestedOptions = {},
) {
  /*
  return format
    {
      community_report: {
        TOTAL: {
          options: null,
        },
        LINE_CHART: {
          options: null,
        },
        TABLE: {
          options: [
            { value: 'BRAND', label: 'by Brand', formattedValue: 'Brand' },
            { value: 'CHANNEL', label: 'by Channel', formattedValue: 'Channel' },
            ...
          ]
        },
        VERTICAL_BAR_CHART: {
          options: [...]
        }
      },
      tsi_report: {
        TOTAL: { options: null },
        LINE_CHART: { options: null },
        ...
      },
      ...
    }
  */

  if (!breakdownOptions) {
    return {};
  }

  const result: Record<string, Record<string, { options: AggregationOption[] | null }>> = {};

  // Get primary aggregation options from backend
  const primaryAggregations = buildPrimaryAggregationOptions(breakdownOptions, {
    featureFlags: options.featureFlags,
    ensureAlwaysShown: options.ensureAlwaysShown,
  });

  // Build for each report category
  Object.entries(categoryVisualizationMap).forEach(([categoryId, visualizationTypes]) => {
    result[categoryId] = {};

    // Build for each visualization option within this category
    visualizationTypes.forEach((visualizationType) => {
      // TOTAL and LINE_CHART (in categories without graph and line chart options) don't have breakdown options (options: null)
      const isLineOrGraph =
        visualizationType === VISUALIZATION_OPTIONS.LINE_CHART ||
        visualizationType === VISUALIZATION_OPTIONS.GRAPH;
      if (
        visualizationType === VISUALIZATION_OPTIONS.TOTAL ||
        (CATEGORIES_WITHOUT_GRAPH_LINE_STACKED_BREAKDOWNS.includes(categoryId) &&
          ((isLineOrGraph && !options.enableLineChartBreakdown) ||
            visualizationType === VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH))
      ) {
        result[categoryId][visualizationType] = {
          options: null,
        };
      } else {
        // Filter out primary aggregations that are not available for the visualization type
        // For example, AGE and GENDER are not available for LINE_CHART and GRAPH visualization types
        const filteredPrimaryAggregations = primaryAggregations.filter(
          (aggregation) => !aggregation.hideByVisualizationTypes?.includes(visualizationType),
        );
        result[categoryId][visualizationType] = {
          options: filteredPrimaryAggregations,
        };
      }
    });
  });

  return result;
}

/**
 * Get secondary breakdown options for a specific primary aggregation
 */
export function getSecondaryBreakdownOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
  primaryAggregation: string | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object' || !primaryAggregation) {
    return [];
  }

  const primaryConfig = breakdownOptions[primaryAggregation];

  if (!primaryConfig?.secondary_breakdown_options) {
    return [];
  }

  return primaryConfig.secondary_breakdown_options;
}

// Maps each flag-gated dimension to the LaunchDarkly flag key that enables it.
// Adding a new gated dimension = one entry here.
export const BREAKDOWN_FEATURE_FLAGS: Record<string, string> = {
  CONTENT_TAG: 'newStatsEngineEnabled',
  MEDIA_TYPE: 'newStatsEngineEnabled',
  FOLLOWER_STATUS: 'newStatsEngineEnabled',
};

export const FEATURE_FLAGGED_DIMENSIONS = new Set(Object.keys(BREAKDOWN_FEATURE_FLAGS));

type FeatureFlags = Record<string, boolean | undefined>;

// Dimensions that should always appear in breakdown dropdowns when their feature
// flag is on — they get rendered disabled (with tooltip) for metrics that don't
// support them, instead of being removed from the list. Limited to flag-gated
// dimensions that have a matching metric-support disable rule, so the injected
// option renders disabled rather than enabled-but-non-functional.
const ALWAYS_SHOWN_DIMENSIONS = [
  BASE_BREAKDOWN_OPTIONS.CONTENT_TAG.value,
  BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE.value,
] as const;

function ensureAlwaysShownDimensions(
  options: AggregationOption[],
  sourceMap: Record<string, AggregationOption>,
  featureFlags: FeatureFlags,
): AggregationOption[] {
  const present = new Set(options.map((option) => option.value));
  const augmented = [...options];
  ALWAYS_SHOWN_DIMENSIONS.forEach((dimension) => {
    const candidate = sourceMap[dimension];
    if (!candidate) return;
    if (present.has(candidate.value)) return;
    if (!hasFlagForDimension(dimension, featureFlags)) return;
    augmented.push(candidate);
  });
  // Sort by canonical source-map position so injected items slot in next to
  // their natural neighbours instead of being pinned to the bottom.
  const canonicalOrder = Object.keys(sourceMap);
  return augmented.sort(
    (a, b) => canonicalOrder.indexOf(a.value) - canonicalOrder.indexOf(b.value),
  );
}

function hasFlagForDimension(dimension: string, featureFlags: FeatureFlags): boolean {
  if (!FEATURE_FLAGGED_DIMENSIONS.has(dimension)) return true;
  const flagName = BREAKDOWN_FEATURE_FLAGS[dimension];
  return Boolean(featureFlags[flagName]);
}

/**
 * Removes `breakdown_options` entries whose dimension is gated by a feature
 * flag the user doesn't have enabled.
 */
export function filterBreakdownOptionsByFlags(
  options: BreakdownOptions | undefined,
  featureFlags: FeatureFlags,
): BreakdownOptions | undefined {
  if (!options) return options;
  return Object.fromEntries(
    Object.entries(options).filter(([dimension]) => hasFlagForDimension(dimension, featureFlags)),
  );
}

/**
 * Build secondary breakdown options from backend breakdown options
 * Maps backend secondary keys to full enum objects.
 * Filters out dimensions whose feature flag the user doesn't have enabled.
 * Pass `featureFlags: {}` (or omit) to exclude all flag-gated dimensions.
 * Pass `ensureAlwaysShown: true` to keep CONTENT_TAG / MEDIA_TYPE present even
 * when the metric omits them — UI marks them disabled with tooltip instead.
 */
export function buildSecondaryBreakdownOptions(
  secondaryBreakdownOptions: string[] | null | undefined,
  visualizationType: VISUALIZATION_OPTIONS,
  { featureFlags = {}, ensureAlwaysShown = false }: BreakdownOptionsBag = {},
): AggregationOption[] {
  if (!secondaryBreakdownOptions) {
    return [];
  }

  // If only NONE is available, return just NONE
  if (secondaryBreakdownOptions.length === 1 && secondaryBreakdownOptions[0] === 'NONE') {
    return [];
  }

  // No secondary breakdown options for GRAPH and LINE_CHART
  if (
    visualizationType === VISUALIZATION_OPTIONS.GRAPH ||
    visualizationType === VISUALIZATION_OPTIONS.LINE_CHART
  ) {
    return [];
  }
  const mapped = secondaryBreakdownOptions
    .map((option) => SECONDARY_BREAKDOWN_MAP[option])
    // removes the option if it is hidden by the visualization type. e.g. LINE_CHART and GRAPH
    .filter((option) => option && !option.hideByVisualizationTypes?.includes(visualizationType))
    // removes the option if it requires a feature flag the user does not have
    .filter((option) => hasFlagForDimension(option.value, featureFlags));

  if (!ensureAlwaysShown) return mapped;
  return ensureAlwaysShownDimensions(mapped, SECONDARY_BREAKDOWN_MAP, featureFlags).filter(
    (option) => !option.hideByVisualizationTypes?.includes(visualizationType),
  );
}

// Stored `report_visualization` is the option `value` (e.g. 'GRAPH'); the breakdown
// map is keyed by `key` (e.g. 'LINE_CHART'). Map value→key, unknown falls back to itself.
export const VISUALIZATION_VALUE_TO_KEY: Record<string, string> = Object.freeze(
  [
    ...Object.values(GRAPH_DISPLAY_OPTIONS_FOR_MENU),
    ...Object.values(METRIC_DISPLAY_OPTIONS),
  ].reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.key;
    return acc;
  }, {}),
);

export interface VisualizationBreakdownContext {
  isMetricReport: boolean;
  isGraphReport: boolean;
  isTSIReportCategory: boolean;
  isCompetitiveCategory: boolean;
  isCommunityReportCategory: boolean;
  isOwnedReportCategory: boolean;
  isCreatorReport: boolean;
  canAccessStatsEngineV2: boolean;
  filters?: { message_types?: string[] } | Record<string, unknown> | null;
  disabledFilters?: string[];
}

function isLineChartVisualization(value: string): boolean {
  return value === GRAPH_DISPLAY_OPTIONS.LINE_CHART.value;
}

function isDoughnutChartVisualization(value: string): boolean {
  return value === GRAPH_DISPLAY_OPTIONS_FOR_MENU.DOUGHNUT_CHART.value;
}

function isHorizontalChartVisualization(value: string): boolean {
  return value === GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value;
}

function formatBreakdownLabels(
  options: AggregationOption[],
  visualizationValue: string,
  isMetricReport: boolean,
): AggregationOption[] {
  return isMetricReport
    ? getMetricReportAggregateTypeOptions(options)
    : getGraphReportAggregateTypeOptions(
        options,
        isLineChartVisualization(visualizationValue),
        isDoughnutChartVisualization(visualizationValue),
        isHorizontalChartVisualization(visualizationValue),
      );
}

function sortBreakdownByTotalFirst(options: AggregationOption[]): AggregationOption[] {
  return [...options].sort((a, b) => {
    if (a.value === BASE_BREAKDOWN_OPTIONS.TOTAL.value) return -1;
    if (b.value === BASE_BREAKDOWN_OPTIONS.TOTAL.value) return 1;
    return 0;
  });
}

function addTotalBreakdownForLineChart(
  options: AggregationOption[],
  visualizationValue: string,
  ctx: VisualizationBreakdownContext,
): AggregationOption[] {
  if (
    ctx.canAccessStatsEngineV2 &&
    (ctx.isOwnedReportCategory || ctx.isCompetitiveCategory || ctx.isCreatorReport) &&
    visualizationValue === GRAPH_DISPLAY_OPTIONS.LINE_CHART.value
  ) {
    return [BASE_BREAKDOWN_OPTIONS.TOTAL, ...options];
  }
  return options;
}

function filterBreakdownAggregations(
  options: AggregationOption[],
  ctx: VisualizationBreakdownContext,
): AggregationOption[] {
  let filtered = options;

  const messageTypes = (ctx.filters as { message_types?: string[] } | null)?.message_types ?? [];
  const sentimentUnavailableMessageTypes = [
    MESSAGE_TYPE_FILTER_OPTIONS.CONVERSATION.statsValue,
    MESSAGE_TYPE_FILTER_OPTIONS.STORY_MENTION.statsValue,
    MESSAGE_TYPE_FILTER_OPTIONS.RETWEET.statsValue,
  ];
  if (messageTypes.some((type) => sentimentUnavailableMessageTypes.includes(type))) {
    filtered = filtered.filter(
      ({ value }) => value !== COMMUNITY_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value,
    );
  }

  const disabledFilters = ctx.disabledFilters ?? [];
  if (disabledFilters.includes('sentiment')) {
    filtered = filtered.filter(
      ({ value }) => value !== COMMUNITY_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value,
    );
  }

  // Remove when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
  if (!ctx.canAccessStatsEngineV2 && ctx.isCommunityReportCategory && ctx.isGraphReport) {
    filtered = filtered.filter(
      ({ value }) =>
        value === COMMUNITY_REPORT_AGGREGATE_BY_TYPES.BRAND.value ||
        value === COMMUNITY_REPORT_AGGREGATE_BY_TYPES.CHANNEL.value,
    );
  }

  const disabledFilterValues = new Set(disabledFilters);
  filtered = filtered.filter(({ value }) => !disabledFilterValues.has(value));

  if (ctx.isTSIReportCategory || (ctx.isMetricReport && !ctx.canAccessStatsEngineV2)) {
    filtered = filtered.filter((option) => option.value !== AGGREGATE_REPORT_TYPES.TAG.value);
  }

  if (ctx.isCompetitiveCategory) {
    filtered = filtered.filter(
      (option) =>
        option.value !== AGGREGATE_REPORT_TYPES.CHANNEL.value &&
        option.value !== BASE_BREAKDOWN_OPTIONS.CAMPAIGN.value,
    );
  }

  return filtered;
}

// Shared by the visualization-selector dropdown and the report-form validator so
// both derive breakdowns identically. Returns [] when the visualization has none.
export function resolveVisualizationBreakdownOptions(
  breakdownOptionsForVisualization: AggregationOption[] | null | undefined,
  visualizationValue: string,
  ctx: VisualizationBreakdownContext,
  applyBreakdownRules: (option: AggregationOption) => AggregationOption,
): AggregationOption[] {
  if (!breakdownOptionsForVisualization?.length) return [];
  const formatted = formatBreakdownLabels(
    breakdownOptionsForVisualization,
    visualizationValue,
    ctx.isMetricReport,
  );
  const filtered = filterBreakdownAggregations(formatted, ctx);
  const withTotal = addTotalBreakdownForLineChart(filtered, visualizationValue, ctx);
  const sorted = sortBreakdownByTotalFirst(withTotal);
  return sorted.map((item) => applyBreakdownRules(item));
}
